/**
 * GhostDerivationService
 *
 * E018-S009: Pure function service that derives required ghosts from block state.
 * NO SIDE EFFECTS - only computes what ghosts should exist.
 */

import { I18n } from '@affine/i18n';

import type {
  IGhostDerivationService,
  IncomingAutomationInfo,
  TrashedLocalAutomationInfo,
  WorkspaceDatabaseInfo,
} from '../core/interfaces/IGhostDerivationService';
import type {
  GhostDefinition,
  RegisteredAutomation,
  RegisteredDatabase,
} from '../core/types';

const RESTRICTED_DOC_TITLE = 'Restricted document';
const RESTRICTED_DATABASE_TITLE = 'Restricted database';
const RESTRICTED_AUTOMATION_TITLE = 'Restricted automation';
const ANOTHER_DOC_TITLE = 'Another Document';
const EXTERNAL_DATABASE_TITLE = 'External Database';
const EXTERNAL_AUTOMATION_TITLE = 'External Automation';

function tGhost(key: string, fallback: string): string {
  const translate = (I18n as unknown as Record<string, () => string>)[key];
  if (typeof translate !== 'function') {
    return fallback;
  }
  const translated = translate();
  return translated && translated !== key ? translated : fallback;
}

function getRestrictedDocTitle(): string {
  return tGhost(
    'com.streamlain.streamlain.ghost.restricted-document',
    RESTRICTED_DOC_TITLE
  );
}

function getRestrictedDatabaseTitle(): string {
  return tGhost(
    'com.streamlain.streamlain.ghost.restricted-database',
    RESTRICTED_DATABASE_TITLE
  );
}

function getRestrictedAutomationTitle(): string {
  return tGhost(
    'com.streamlain.streamlain.ghost.restricted-automation',
    RESTRICTED_AUTOMATION_TITLE
  );
}

function getAnotherDocTitle(): string {
  return tGhost(
    'com.streamlain.streamlain.ghost.another-document',
    ANOTHER_DOC_TITLE
  );
}

function getExternalDatabaseTitle(): string {
  return tGhost(
    'com.streamlain.streamlain.ghost.external-database',
    EXTERNAL_DATABASE_TITLE
  );
}

function getExternalAutomationTitle(): string {
  return tGhost(
    'com.streamlain.streamlain.ghost.external-automation',
    EXTERNAL_AUTOMATION_TITLE
  );
}

// =============================================================================
// Implementation
// =============================================================================

/**
 * Service that derives which ghosts are needed based on current block state.
 *
 * This is a PURE FUNCTION service - no side effects, fully testable.
 */
export class GhostDerivationService implements IGhostDerivationService {
  /**
   * Derives which ghosts are required based on current block state.
   */
  deriveRequiredGhosts(
    databases: RegisteredDatabase[],
    automations: RegisteredAutomation[],
    workspaceDatabases: WorkspaceDatabaseInfo[],
    incomingAutomations?: IncomingAutomationInfo[],
    trashedLocalAutomations?: TrashedLocalAutomationInfo[],
    currentDocId?: string
  ): GhostDefinition[] {
    const ghosts: GhostDefinition[] = [];
    const onCanvasDbIds = new Set(databases.map(db => db.blockId));
    const workspaceDbMap = new Map(
      workspaceDatabases.map(db => [db.blockId, db])
    );

    // E018: Create set of all existing database IDs (active + trashed)
    // Used to filter out permanently deleted databases
    const existingDbIds = new Set(workspaceDatabases.map(db => db.blockId));

    // Track which targets already have ghosts to avoid duplicates
    const ghostedTargets = new Set<string>();

    // PART 1: Outgoing relations (Local DB → External DB)
    for (const db of databases) {
      for (const rel of db.relations) {
        const targetId = rel.targetDatabaseId;

        // Skip if target is on canvas (no ghost needed)
        if (onCanvasDbIds.has(targetId)) continue;

        // If a target doc is known but the database is absent from readable
        // discovery, keep the structural edge as a restricted ghost. Without a
        // target doc there is no safe way to distinguish deletion from no access.
        if (!existingDbIds.has(targetId) && !rel.targetDocId) continue;

        // Skip if already ghosted
        if (ghostedTargets.has(targetId)) continue;

        const targetInfo = workspaceDbMap.get(targetId);

        // FIX: Skip if target is in SAME doc but not on canvas and not yet in trashcan
        // This prevents ghost from appearing at wrong position before trashcan scan completes
        if (
          targetInfo &&
          targetInfo.docId === currentDocId &&
          !targetInfo.isInTrash
        ) {
          // Target is in same doc, not on canvas, and not marked as trashed yet
          // This means the block was just deleted and trashcan scan hasn't completed
          // Skip for now - ghost will be created after trashcan scan
          continue;
        }

        const ghost = this.createRelationGhost(
          db,
          targetId,
          targetInfo,
          'relation-outgoing',
          rel.targetDocId
        );
        ghosts.push(ghost);
        ghostedTargets.add(targetId);
      }
    }

    // PART 2: Incoming relations (External DB → Local DB)
    // NOTE: This requires checking workspace DB relations, which is handled
    // by the EdgeOrchestrator using findIncomingRelations callback.
    // The incoming relations are passed via a separate mechanism.

    // PART 3: Outgoing automations (Local Automation → External DB)
    for (const auto of automations) {
      // sl-m379: Check all input tables (lead sourceTableId + secondary inputTables)
      const inputTableRefs: Array<{ tableId: string; docId?: string | null }> =
        [];
      if (auto.sourceTableId) {
        inputTableRefs.push({
          tableId: auto.sourceTableId,
          docId: auto.sourceDocId,
        });
      }
      for (const inputTable of auto.executionKind === 'cleanup'
        ? []
        : (auto.inputTables ?? [])) {
        if (inputTable.tableId) {
          inputTableRefs.push({
            tableId: inputTable.tableId,
            docId: inputTable.docId,
          });
        }
      }

      for (const { tableId: inputTableId, docId } of inputTableRefs) {
        if (
          onCanvasDbIds.has(inputTableId) ||
          ghostedTargets.has(inputTableId)
        ) {
          continue;
        }

        if (!existingDbIds.has(inputTableId) && !docId) continue;

        const targetInfo = workspaceDbMap.get(inputTableId);

        // FIX: Skip if target is in SAME doc but not on canvas and not yet in trashcan
        if (
          targetInfo &&
          targetInfo.docId === currentDocId &&
          !targetInfo.isInTrash
        ) {
          continue;
        }

        const ghost = this.createAutomationGhost(
          auto,
          inputTableId,
          targetInfo,
          'automation-outgoing',
          'left', // Input comes from left
          docId ?? undefined
        );
        ghosts.push(ghost);
        ghostedTargets.add(inputTableId);
      }

      // sl-4n0: Check each output table
      for (const outputTable of auto.outputTables ?? []) {
        if (
          !onCanvasDbIds.has(outputTable.tableId) &&
          !ghostedTargets.has(outputTable.tableId)
        ) {
          if (!existingDbIds.has(outputTable.tableId) && !outputTable.docId) {
            continue;
          }

          const targetInfo = workspaceDbMap.get(outputTable.tableId);

          // FIX: Skip if target is in SAME doc but not on canvas and not yet in trashcan
          if (
            targetInfo &&
            targetInfo.docId === currentDocId &&
            !targetInfo.isInTrash
          ) {
            continue;
          }

          const ghost = this.createAutomationGhost(
            auto,
            outputTable.tableId,
            targetInfo,
            'automation-outgoing',
            'right', // Output goes to right
            outputTable.docId
          );
          ghosts.push(ghost);
          ghostedTargets.add(outputTable.tableId);
        }
      }
    }

    // PART 4: Incoming automations (External Automation → Local DB)
    // E018-S014: Each incoming automation can reference MULTIPLE local DBs
    // (e.g., same automation has sourceTableId AND targetTableId in this doc)
    // We create ONE ghost per automation, but track ALL connections for edge derivation.
    if (incomingAutomations) {
      // Group by automationId to handle multi-DB case
      const automationGroups = new Map<
        string,
        { incoming: IncomingAutomationInfo; localDb: RegisteredDatabase }[]
      >();

      for (const incoming of incomingAutomations) {
        const localDb = databases.find(
          db => db.blockId === incoming.localDatabaseId
        );
        if (!localDb) continue;

        const group = automationGroups.get(incoming.automationId) || [];
        group.push({ incoming, localDb });
        automationGroups.set(incoming.automationId, group);
      }

      // Create ONE ghost per automation, using first DB as position reference
      for (const [automationId, group] of automationGroups) {
        if (ghostedTargets.has(automationId)) continue;

        const firstEntry = group[0];
        const { incoming, localDb } = firstEntry;

        // E018-S014: Store ALL connections in ghost for edge derivation
        // For now, we use the first connection's direction for preferredSide
        // But EdgeDerivationService will use incomingAutomations to create ALL edges
        const canReadIncoming = incoming.accessState === 'read';
        const ghost: GhostDefinition = {
          // Canonical persisted identity: sourceDatabaseId-targetDatabaseId.
          // GhostSync reconstructs the same key from the stored block props.
          key: `${localDb.blockId}-${automationId}`,
          sourceBlockId: localDb.blockId,
          sourceElementId: localDb.elementId,
          targetId: automationId,
          type: 'automation-incoming',
          targetDocId: incoming.docId,
          targetDocTitle: canReadIncoming
            ? incoming.docTitle
            : getRestrictedDocTitle(),
          targetTitle: canReadIncoming
            ? incoming.automationName
            : getRestrictedAutomationTitle(),
          targetAccessState: canReadIncoming ? 'read' : 'restricted',
          // Position hint - use 'right' if has input, 'left' if only output
          preferredSide: group.some(g => g.incoming.direction === 'source')
            ? 'right'
            : 'left',
        };
        ghosts.push(ghost);
        ghostedTargets.add(automationId);
      }
    }

    // PART 5: Trashed local automations (Deleted Automation → Local DB on canvas)
    // When an automation in the SAME doc is deleted but had connections to DBs still on canvas
    if (trashedLocalAutomations) {
      // Group by automationId to handle multi-DB case (same automation connected to multiple DBs)
      const trashedGroups = new Map<
        string,
        { trashed: TrashedLocalAutomationInfo; localDb: RegisteredDatabase }[]
      >();

      for (const trashed of trashedLocalAutomations) {
        const localDb = databases.find(
          db => db.blockId === trashed.localDatabaseId
        );
        if (!localDb) continue;

        const group = trashedGroups.get(trashed.automationId) || [];
        group.push({ trashed, localDb });
        trashedGroups.set(trashed.automationId, group);
      }

      // Create ONE ghost per trashed automation
      for (const [automationId, group] of trashedGroups) {
        if (ghostedTargets.has(automationId)) continue;

        const firstEntry = group[0];
        const { trashed, localDb } = firstEntry;

        // Use stable key based only on automationId to prevent duplicates
        // when the same automation is connected to multiple DBs
        const ghost: GhostDefinition = {
          key: `trashed-auto-${automationId}`,
          sourceBlockId: localDb.blockId,
          sourceElementId: localDb.elementId,
          targetId: automationId,
          type: 'automation-incoming', // Treated as incoming since it's external (in trash)
          targetDocId: trashed.docId,
          targetDocTitle: trashed.docTitle,
          targetTitle: trashed.automationName,
          // Position hint - use 'right' if was input, 'left' if was output
          preferredSide: group.some(g => g.trashed.direction === 'source')
            ? 'right'
            : 'left',
          isTargetInTrash: true, // Always true - this is a trashed automation
          // Use original position from snapshot to place ghost where automation was
          originalPosition: trashed.originalPosition,
        };
        ghosts.push(ghost);
        ghostedTargets.add(automationId);
      }
    }

    return ghosts;
  }

  /**
   * Creates a ghost definition for a relation target.
   * Key format matches GhostNodeService: ${sourceDatabaseId}-${targetDatabaseId}
   */
  private createRelationGhost(
    sourceDb: RegisteredDatabase,
    targetId: string,
    targetInfo: WorkspaceDatabaseInfo | undefined,
    type: 'relation-outgoing' | 'relation-incoming',
    targetDocId?: string
  ): GhostDefinition {
    const isRestricted =
      targetInfo?.accessState === 'restricted' ||
      (!targetInfo && !!targetDocId);
    return {
      // Key format MUST match scanExistingGhosts in GhostSyncService
      key: `${sourceDb.blockId}-${targetId}`,
      sourceBlockId: sourceDb.blockId,
      sourceElementId: sourceDb.elementId,
      targetId,
      type,
      targetDocId: targetInfo?.docId ?? targetDocId ?? 'unknown',
      targetDocTitle: isRestricted
        ? getRestrictedDocTitle()
        : (targetInfo?.docTitle ?? getAnotherDocTitle()),
      targetTitle: isRestricted
        ? getRestrictedDatabaseTitle()
        : (targetInfo?.databaseTitle ?? getExternalDatabaseTitle()),
      targetAccessState: isRestricted
        ? 'restricted'
        : (targetInfo?.accessState ??
          (targetInfo?.isInTrash ? 'trashed' : 'read')),
      preferredSide: 'right', // Relations typically go to the right
      isTargetInTrash: targetInfo?.isInTrash,
      // Use original position if target is in local trashcan
      originalPosition: targetInfo?.originalPosition,
    };
  }

  /**
   * Creates a ghost definition for an automation target.
   * Key format matches GhostNodeService: ${sourceDatabaseId}-${targetDatabaseId}
   */
  private createAutomationGhost(
    sourceAuto: RegisteredAutomation,
    targetId: string,
    targetInfo: WorkspaceDatabaseInfo | undefined,
    type: 'automation-outgoing' | 'automation-incoming',
    preferredSide: 'left' | 'right',
    targetDocId?: string
  ): GhostDefinition {
    const isRestricted =
      targetInfo?.accessState === 'restricted' ||
      (!targetInfo && !!targetDocId);
    return {
      // Key format MUST match scanExistingGhosts in GhostSyncService
      key: `${sourceAuto.blockId}-${targetId}`,
      sourceBlockId: sourceAuto.blockId,
      sourceElementId: sourceAuto.elementId,
      targetId,
      type,
      targetDocId: targetInfo?.docId ?? targetDocId ?? 'unknown',
      targetDocTitle: isRestricted
        ? getRestrictedDocTitle()
        : (targetInfo?.docTitle ?? getAnotherDocTitle()),
      targetTitle: isRestricted
        ? getRestrictedDatabaseTitle()
        : (targetInfo?.databaseTitle ?? getExternalAutomationTitle()),
      targetAccessState: isRestricted
        ? 'restricted'
        : (targetInfo?.accessState ??
          (targetInfo?.isInTrash ? 'trashed' : 'read')),
      preferredSide,
      isTargetInTrash: targetInfo?.isInTrash,
      // Use original position if target is in local trashcan
      originalPosition: targetInfo?.originalPosition,
    };
  }
}
